/**
 * ResetPassword.jsx
 * Handles the password reset link from email.
 * Supabase redirects here with a token in the URL.
 *
 * Add to App.jsx:
 *   import ResetPassword from "./pages/ResetPassword";
 *   <Route path="/reset-password" element={<ResetPassword />} />
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password,        setPassword]        = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword,    setShowPassword]    = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState("");
  const [success,         setSuccess]         = useState(false);
  const [validSession,    setValidSession]    = useState(false);
  const [checking,        setChecking]        = useState(true);

  useEffect(() => {
    // Supabase puts the token in the URL hash — it auto-creates a session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setValidSession(true);
      } else {
        setError("Invalid or expired reset link. Please request a new one.");
      }
      setChecking(false);
    };

    // Listen for the PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setValidSession(true);
        setChecking(false);
      }
    });

    checkSession();
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6)          { setError("Password must be at least 6 characters."); return; }
    if (password !== confirmPassword)  { setError("Passwords do not match."); return; }

    setLoading(true);
    setError("");
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) return (
    <div style={{
      minHeight: "100vh", background: "#0a0b0f",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{ width: 32, height: 32, border: "3px solid #6366f1", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{
      minHeight: "100vh", background: "#0a0b0f",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "sans-serif", padding: 24,
    }}>
      <div style={{
        background: "#12141a", border: "1px solid #2a2d3a",
        borderRadius: 16, padding: "40px 36px",
        width: "100%", maxWidth: 420,
      }}>

        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 11,
            background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 900, color: "#fff",
          }}>N</div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>
              Reset Password
            </h1>
            <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
              Enter your new password below
            </p>
          </div>
        </div>

        {success ? (
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px", fontSize: 24,
            }}>✅</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: "#f1f5f9", margin: "0 0 8px" }}>
              Password Updated!
            </h2>
            <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.7 }}>
              Your password has been reset successfully. Redirecting to login…
            </p>
            <div style={{
              width: 28, height: 28, border: "3px solid #4ade80",
              borderTopColor: "transparent", borderRadius: "50%",
              animation: "spin 0.8s linear infinite", margin: "20px auto 0",
            }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : !validSession ? (
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px", fontSize: 24,
            }}>❌</div>
            <p style={{ fontSize: 13, color: "#f87171", margin: "0 0 20px", lineHeight: 1.7 }}>
              {error}
            </p>
            <a href="/forgot-password" style={{
              display: "inline-block", padding: "10px 24px", borderRadius: 8,
              background: "#6366f1", color: "#fff",
              fontSize: 13, fontWeight: 700, textDecoration: "none",
            }}>
              Request New Link
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* New password */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                New Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="password" name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  style={{
                    width: "100%", padding: "10px 44px 10px 14px", borderRadius: 9,
                    border: "1px solid #2a2d3a", background: "#1a1d26",
                    color: "#f1f5f9", fontSize: 13, outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute", right: 12, top: "50%",
                    transform: "translateY(-50%)",
                    background: "none", border: "none",
                    cursor: "pointer", color: "#64748b",
                    display: "flex", alignItems: "center",
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                Confirm New Password
              </label>
              <input
                id="confirmPassword" name="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                required
                style={{
                  width: "100%", padding: "10px 14px", borderRadius: 9,
                  border: `1px solid ${confirmPassword && password !== confirmPassword ? "rgba(248,113,113,0.5)" : "#2a2d3a"}`,
                  background: "#1a1d26", color: "#f1f5f9",
                  fontSize: 13, outline: "none", boxSizing: "border-box",
                }}
              />
              {confirmPassword && password !== confirmPassword && (
                <p style={{ fontSize: 11, color: "#f87171", margin: "4px 0 0" }}>Passwords do not match</p>
              )}
            </div>

            {error && (
              <div style={{
                background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)",
                borderRadius: 8, padding: "10px 14px",
                fontSize: 12, color: "#f87171",
              }}>{error}</div>
            )}

            {/* Password strength indicator */}
            <div>
              <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                {[1,2,3,4].map(i => (
                  <div key={i} style={{
                    flex: 1, height: 3, borderRadius: 2,
                    background: password.length >= i * 3
                      ? i <= 1 ? "#f87171" : i <= 2 ? "#fbbf24" : i <= 3 ? "#4ade80" : "#818cf8"
                      : "#2a2d3a",
                    transition: "background 0.2s",
                  }} />
                ))}
              </div>
              <p style={{ fontSize: 10, color: "#64748b", margin: 0 }}>
                {password.length === 0 ? "Enter a password" :
                 password.length < 6  ? "Too short" :
                 password.length < 9  ? "Fair" :
                 password.length < 12 ? "Good" : "Strong"}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || password !== confirmPassword || password.length < 6}
              style={{
                padding: "12px", borderRadius: 10, border: "none",
                background: loading || password !== confirmPassword || password.length < 6
                  ? "#374151" : "#6366f1",
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: loading || password !== confirmPassword || password.length < 6
                  ? "not-allowed" : "pointer",
                marginTop: 4,
              }}
            >
              {loading ? "Updating password…" : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
